import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  api,
  AcceleratorGrantAgent,
  AcceleratorGrantFeature,
  AcceleratorInvitee,
} from '../../services/api';
import { getPhotoUrl } from '../../../utils/formatting';

interface AcceleratorGrantAccessDialogProps {
  open: boolean;
  onClose: () => void;
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-SG', { year: 'numeric', month: 'short', day: '2-digit' });
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

type PendingRevoke = { grantId: number; label: string; name: string };

/**
 * "Accelerator → Feature Release" — a Super Accelerator keys in an agent's CEA or
 * mobile and grants them features that are not yet released to everyone (Good
 * Deals Map, Decoupling Calculator). Lists the accelerator's own invitees, each
 * feature revocable. The server writes and removes the access rows.
 */
const AcceleratorGrantAccessDialog: React.FC<AcceleratorGrantAccessDialogProps> = ({ open, onClose }) => {
  const [features, setFeatures] = useState<{ key: AcceleratorGrantFeature; label: string }[]>([]);
  const [invitees, setInvitees] = useState<AcceleratorInvitee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [identifier, setIdentifier] = useState('');
  const [looking, setLooking] = useState(false);
  const [agent, setAgent] = useState<AcceleratorGrantAgent | null>(null);
  const [selected, setSelected] = useState<Set<AcceleratorGrantFeature>>(new Set());
  const [granting, setGranting] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<PendingRevoke | null>(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listAcceleratorGrants();
      setFeatures(data.features);
      setInvitees(data.invitees);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load your invites'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  // Features this accelerator has already given the looked-up agent.
  const alreadyGranted = useMemo(() => {
    if (!agent) return new Set<AcceleratorGrantFeature>();
    const invitee = invitees.find((i) => i.ceaNumber === agent.ceaNumber);
    return new Set(invitee?.features.map((f) => f.feature) ?? []);
  }, [agent, invitees]);

  const handleLookup = async () => {
    const value = identifier.trim();
    if (!value) return;
    setLooking(true);
    setError(null);
    setSuccess(null);
    setAgent(null);
    setSelected(new Set());
    try {
      setAgent(await api.lookupAcceleratorGrantAgent(value));
    } catch (err) {
      setError(errorMessage(err, 'Failed to look up agent'));
    } finally {
      setLooking(false);
    }
  };

  const toggleFeature = (key: AcceleratorGrantFeature) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleGrant = async () => {
    if (!agent || selected.size === 0) return;
    setGranting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.grantAcceleratorFeatures(agent.ceaNumber, Array.from(selected));
      setInvitees(result.invitees);
      const labels = result.granted.map((key) => features.find((f) => f.key === key)?.label ?? key);
      setSuccess(labels.length
        ? `${agent.name || agent.ceaNumber} now has ${labels.join(' and ')}.`
        : `${agent.name || agent.ceaNumber} already had those features from you.`);
      setAgent(null);
      setIdentifier('');
      setSelected(new Set());
    } catch (err) {
      setError(errorMessage(err, 'Failed to grant access'));
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async () => {
    if (!pendingRevoke) return;
    setRevoking(true);
    setError(null);
    setSuccess(null);
    try {
      setInvitees(await api.revokeAcceleratorGrant(pendingRevoke.grantId));
      setPendingRevoke(null);
    } catch (err) {
      setError(errorMessage(err, 'Failed to revoke access'));
      setPendingRevoke(null);
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
          minHeight: '70vh',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h6">Feature Release</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
              Give an agent early access to features not yet released to everyone.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={() => void load()} size="small" disabled={loading} title="Refresh">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: '16px !important' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="CEA number or mobile"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleLookup(); }}
          />
          <Button
            variant="contained"
            onClick={() => void handleLookup()}
            disabled={looking || !identifier.trim()}
            startIcon={looking ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            sx={{ flexShrink: 0 }}
          >
            Find
          </Button>
        </Stack>

        {agent && (
          <Box sx={{ p: 2, mb: 3, border: '1px solid rgba(var(--color-text-rgb), 0.12)', borderRadius: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Avatar src={getPhotoUrl(agent.photo)} alt={agent.name ?? agent.ceaNumber} />
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} noWrap>{agent.name || agent.ceaNumber}</Typography>
                <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }} noWrap>
                  {[agent.ceaNumber, agent.mobile, agent.designation].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </Stack>
            <Stack>
              {features.map((f) => {
                const has = alreadyGranted.has(f.key);
                return (
                  <FormControlLabel
                    key={f.key}
                    control={(
                      <Checkbox
                        checked={has || selected.has(f.key)}
                        disabled={has}
                        onChange={() => toggleFeature(f.key)}
                      />
                    )}
                    label={has ? `${f.label} (already granted)` : f.label}
                  />
                );
              })}
            </Stack>
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 1 }}
              onClick={() => void handleGrant()}
              disabled={granting || selected.size === 0}
            >
              {granting ? 'Granting…' : 'Grant access'}
            </Button>
          </Box>
        )}

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          My invites{invitees.length ? ` (${invitees.length})` : ''}
        </Typography>
        {loading && invitees.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : invitees.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 2 }}>
            You haven't granted access to anyone yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {invitees.map((invitee) => (
              <Box
                key={invitee.ceaNumber}
                sx={{ p: 1.5, border: '1px solid rgba(var(--color-text-rgb), 0.08)', borderRadius: 2 }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar src={getPhotoUrl(invitee.photo)} alt={invitee.name ?? invitee.ceaNumber} sx={{ width: 36, height: 36 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={600} noWrap>{invitee.name || invitee.ceaNumber}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }} noWrap component="div">
                      {[invitee.ceaNumber, invitee.mobile].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  {invitee.features.map((f) => (
                    <Chip
                      key={f.grantId}
                      size="small"
                      label={`${f.label} · ${formatDate(f.grantedAt)}`}
                      onDelete={() => setPendingRevoke({
                        grantId: f.grantId,
                        label: f.label,
                        name: invitee.name || invitee.ceaNumber,
                      })}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <Dialog open={pendingRevoke !== null} onClose={() => !revoking && setPendingRevoke(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Revoke access?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {pendingRevoke && `${pendingRevoke.name} will lose ${pendingRevoke.label} unless another accelerator or an admin also granted it.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRevoke(null)} disabled={revoking}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void handleRevoke()} disabled={revoking}>
            {revoking ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AcceleratorGrantAccessDialog;
